import React, { useState, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useNavigate } from 'react-router-dom';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { endPoints } from '../../Config';
import { useDispatch } from 'react-redux';
import AddEditPartsInventory from './AddEditPartsInventory';
import { handleDeleteRequest } from '../../service/DeleteTemplate';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText, translateUiToken } from '../../utilities/siteTranslations';
import SparePartRepairTasks from './SparePartRepairTasks';



const LOW_STOCK_THRESHOLD = 10;

const PartsInventory = () => {
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [rowRecord, setRowRecord] = useState(null);
    const [partsInventory, setPartsInventory] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [requestHistory, setRequestHistory] = useState([]);
    const [inventoryActivity, setInventoryActivity] = useState([]);
    const [editable, setEditable] = useState(false);
    const [viewable, setViewable] = useState(false);
    const [processingRequestId, setProcessingRequestId] = useState('');
    const [activeRequestTab, setActiveRequestTab] = useState('pending');
    const [filters, setFilters] = useState({
        sku: '',
        category: null,
        description: ''
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Check if user is a technician or admin
    const currentUser = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const currentAdmin = currentUser?.data?.adminUser || {};
    const currentRole = currentAdmin?.role || {};
    const currentRoleUid = currentRole?.uid;
    const isTechnician = currentRoleUid === 'technician';
    const isAdmin = currentRoleUid === 'admin' || currentRoleUid === 'superAdmin';
    const canReviewRequests = ['admin', 'superAdmin', 'support'].includes(currentRoleUid);
    const t = (value) => translateUiText(value, preferredLanguage);

    const handleAdd = () => {
        setShowAddDialog(true);
        setEditable(false);
        setViewable(false);
    };

    const handleEdit = (rowData) => {
        setShowAddDialog(true);
        setEditable(true);
        setViewable(false);
        setRowRecord(rowData);
    };

    const handleView = (rowData) => {
        setShowAddDialog(true);
        setEditable(false);
        setViewable(true);
        setRowRecord(rowData);
    };

    const onHide = () => {
        setShowAddDialog(false);
        getPartsInventoryList();
        getInventoryActivity();
    };
    const onClose = () => {
        setShowAddDialog(false);
    };

    const handleDelete = (rowData) => {
        confirmDialog({
            message: `${t('Are you sure you want to delete')} "${rowData.sku}"? ${t('This action cannot be undone.')}`,
            header: t('Confirm Deletion'),
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                const response = await dispatch(handleDeleteRequest(endPoints.deletePartsInventory(rowData._id), true, true));
                if (response?.status === 200) {
                    getPartsInventoryList();
                }
            },
            reject: () => {}
        });
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <>
                <div className="actions">
                    {isAdmin && <Button label="Crear solicitud de compra" icon="pi pi-shopping-cart" onClick={() => navigate(`/purchasing?originType=spare_part&originId=${rowData._id}`)} />}
                    {!isTechnician && (
                        <Button
                            tooltip={t('Edit')}
                            icon="pi pi-pencil"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                                handleEdit(rowData);
                            }}
                            className="p-button-rounded p-button-primary mr-2"
                        />
                    )}
                    <Button
                        tooltip={t('View')}
                        tooltipOptions={{ position: 'top' }}
                        icon="pi pi-eye"
                        onClick={() => {
                            handleView(rowData);
                        }}
                        className="p-button-rounded mr-2"
                        style={{ backgroundColor: '#F2AC57', border: 'none' }}
                    />
                    {isAdmin && (
                        <Button
                            tooltip={t('Delete')}
                            icon="pi pi-trash"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => {
                                handleDelete(rowData);
                            }}
                            className="p-button-rounded p-button-danger"
                        />
                    )}
                </div>
            </>
        );
    };

    const getPartsInventoryList = async () => {
        const response = await handleGetRequest(endPoints.getallPartsInventory, true, false, dispatch);
        if (response) {
            setPartsInventory(response?.data);
        }
    };

    const getPendingRequests = async () => {
        const response = await handleGetRequest(endPoints.getPendingSparePartUsageRequests, true, false, dispatch);
        if (response?.data) {
            setPendingRequests(response.data);
        } else {
            setPendingRequests([]);
        }
    };

    const getRequestHistory = async () => {
        const params = new URLSearchParams({ usageType: 'repair' });
        const response = await handleGetRequest(endPoints.getSparePartUsageReport(params.toString()), true, false, dispatch);
        if (response?.data) {
            setRequestHistory(response.data.filter((row) => row.status !== 'pending'));
        } else {
            setRequestHistory([]);
        }
    };

    const getInventoryActivity = async () => {
        const response = await handleGetRequest(endPoints.getInventoryActivity, true, false, dispatch);
        if (response?.data) {
            setInventoryActivity(response.data);
        } else {
            setInventoryActivity([]);
        }
    };

    const submitReviewRequest = async (rowData, action, comment = '') => {
        const usageId = rowData?._id;
        if (!usageId || !currentAdmin?._id) return;

        setProcessingRequestId(usageId);
        const endpoint = action === 'approve' ? endPoints.approveSparePartUsage : endPoints.rejectSparePartUsage;
        const payload = { usageId, supervisorId: currentAdmin._id, comment };
        const response = await dispatch(handlePostRequest(payload, endpoint, true));
        setProcessingRequestId('');

        if (response?.status === 200) {
            await Promise.all([getPendingRequests(), getRequestHistory(), getInventoryActivity(), getPartsInventoryList()]);
        }
    };

    const handleReviewRequest = (rowData, action) => {
        if (action === 'approve') {
            const currentStock = Number(rowData?.sparePart?.quantityInStock || 0);
            const requestedQty = Number(rowData?.quantity || 0);
            const remainingStock = currentStock - requestedQty;
            const lowStock = remainingStock > 0 && remainingStock < LOW_STOCK_THRESHOLD;
            const depletedStock = remainingStock <= 0;

            confirmDialog({
                header: depletedStock ? t('Approve Request: Stock Will Reach Zero') : lowStock ? t('Approve Request: Low Stock Warning') : t('Approve Spare Part Request'),
                icon: depletedStock || lowStock ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle',
                message: (
                    <div className="flex flex-column gap-2">
                        <div><strong>{t('Current stock:')}</strong> {currentStock}</div>
                        <div><strong>{t('Requested quantity:')}</strong> {requestedQty}</div>
                        <div><strong>{t('Stock after approval:')}</strong> {remainingStock}</div>
                        {depletedStock ? (
                            <div style={{ color: '#dc2626', fontWeight: 600 }}>{t('This approval will leave this part with zero stock.')}</div>
                        ) : lowStock ? (
                            <div style={{ color: '#d97706', fontWeight: 600 }}>{t('This approval will reduce stock below the low-stock threshold of 10.').replace('10', String(LOW_STOCK_THRESHOLD))}</div>
                        ) : null}
                    </div>
                ),
                acceptLabel: t('Approve'),
                rejectLabel: t('Cancel'),
                accept: async () => {
                    const comment = window.prompt(t('Approval comment (optional):'), '') || '';
                    await submitReviewRequest(rowData, action, comment);
                }
            });
            return;
        }

        const comment = window.prompt(t('Reason / comment for rejection (optional):'), '') || '';
        submitReviewRequest(rowData, action, comment);
    };

    const stockStatusTemplate = (rowData) => {
        const currentStock = Number(rowData?.sparePart?.quantityInStock || 0);
        const requestedQty = Number(rowData?.quantity || 0);
        const remainingStock = currentStock - requestedQty;
        const warningColor = remainingStock <= 0 ? '#dc2626' : remainingStock < LOW_STOCK_THRESHOLD ? '#d97706' : '#16a34a';

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                <span>{t('Current:')} {currentStock}</span>
                <span style={{ color: warningColor, fontWeight: 600 }}>{t('After approval:')} {remainingStock}</span>
            </div>
        );
    };

    const openMachineLink = (rowData) => {
        const searchValue = rowData?.machineSerialNumber || rowData?.machine?.serialNumber || rowData?.machineUuid || rowData?.machine?.uuid || rowData?.machineLocation || rowData?.machine?.location || '';
        navigate(`/machine-list?search=${encodeURIComponent(searchValue)}`);
    };

    const sourceLinksTemplate = (rowData) => {
        const repairOrderId = rowData?.repairOrderId || rowData?.repairOrder?._id;
        const hasMachine = Boolean(rowData?.machineId || rowData?.machine?._id || rowData?.machineSerialNumber || rowData?.machine?.serialNumber || rowData?.machineUuid || rowData?.machine?.uuid);

        return (
            <div className="flex gap-2 flex-wrap">
                <Button
                    label={t('Repair Order')}
                    className="p-button-text p-button-sm"
                    disabled={!repairOrderId}
                    onClick={() => navigate(`/repair-orders/${repairOrderId}`)}
                />
                <Button
                    label={t('Machine')}
                    className="p-button-text p-button-sm"
                    disabled={!hasMachine}
                    onClick={() => openMachineLink(rowData)}
                />
            </div>
        );
    };

    const requestActionsTemplate = (rowData) => {
        if (!canReviewRequests) return null;

        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-check"
                    className="p-button-rounded p-button-success p-button-sm"
                    tooltip={t('Approve')}
                    onClick={() => handleReviewRequest(rowData, 'approve')}
                    loading={processingRequestId === rowData._id}
                    disabled={processingRequestId === rowData._id}
                />
                <Button
                    icon="pi pi-times"
                    className="p-button-rounded p-button-danger p-button-sm"
                    tooltip={t('Reject')}
                    onClick={() => handleReviewRequest(rowData, 'reject')}
                    loading={processingRequestId === rowData._id}
                    disabled={processingRequestId === rowData._id}
                />
            </div>
        );
    };

    const categoryOptions = useMemo(() => {
        const values = [...new Set(partsInventory.map((item) => item?.category).filter(Boolean))];
        return values
            .sort((a, b) => a.localeCompare(b))
            .map((value) => ({ label: value, value }));
    }, [partsInventory]);

    const filteredPartsInventory = useMemo(() => {
        const skuFilter = filters.sku.trim().toLowerCase();
        const descriptionFilter = filters.description.trim().toLowerCase();
        const categoryFilter = filters.category;

        return partsInventory.filter((item) => {
            const sku = String(item?.sku || '').toLowerCase();
            const category = String(item?.category || '');
            const description = String(item?.description || '').toLowerCase();

            if (skuFilter && !sku.includes(skuFilter)) return false;
            if (categoryFilter && category !== categoryFilter) return false;
            if (descriptionFilter && !description.includes(descriptionFilter)) return false;

            return true;
        });
    }, [filters, partsInventory]);

    const requestHistoryRows = useMemo(
        () =>
            requestHistory
                .slice()
                .sort((a, b) => new Date(b.authorizationDate || b.createdAt || 0) - new Date(a.authorizationDate || a.createdAt || 0)),
        [requestHistory]
    );

    const inventoryActivityRows = useMemo(
        () =>
            inventoryActivity
                .slice()
                .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
        [inventoryActivity]
    );

    const activityTypeTemplate = (rowData) => {
        const labels = {
            create_part: t('Create Part'),
            manual_adjustment: t('Manual Adjustment'),
            approved_request: t('Approved Request'),
            import_create: t('Import Create'),
            import_update: t('Import Update'),
            repair_return: t('Repair Return')
        };

        return labels[rowData.actionType] || rowData.actionType || '-';
    };

    const quantityDeltaTemplate = (rowData) => {
        const delta = Number(rowData.deltaQuantity || 0);
        const color = delta < 0 ? '#dc2626' : delta > 0 ? '#16a34a' : '#64748b';
        const prefix = delta > 0 ? '+' : '';
        return <span style={{ color, fontWeight: 600 }}>{`${prefix}${delta}`}</span>;
    };

    const activityPerformedByTemplate = (rowData) => {
        return `${rowData.performedBy?.firstName || ''} ${rowData.performedBy?.lastName || ''}`.trim() || rowData.performedBy?.email || '-';
    };

    const refreshInventoryAfterRepair = async () => {
        await Promise.all([getPartsInventoryList(), getInventoryActivity()]);
    };

    useEffect(() => {
        const syncPreferredLanguage = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', syncPreferredLanguage);
        window.addEventListener('storage', syncPreferredLanguage);

        return () => {
            window.removeEventListener('preferred-language-changed', syncPreferredLanguage);
            window.removeEventListener('storage', syncPreferredLanguage);
        };
    }, []);

    useEffect(() => {
        getPartsInventoryList();
        getPendingRequests();
        getRequestHistory();
        getInventoryActivity();
    }, []);

    return (
        <div className="p-4">
            <ConfirmDialog />
            <Dialog
                header={<div>{editable ? t('Edit Parts Inventory') : viewable ? t('View Parts Inventory') : t('Add Parts Inventory')}</div>}
                visible={showAddDialog}
                onHide={onClose}
                breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                style={{ width: '40vw' }}
                draggable={false}
            >
                <AddEditPartsInventory rowRecord={rowRecord} editable={editable} viewable={viewable} onHide={onHide} onClose={onClose} preferredLanguage={preferredLanguage} />
            </Dialog>

            <div className="flex justify-content-between align-items-center mb-3">
                <h3>{t('Parts Inventory')}</h3>

                {/* <div className="flex gap-2">{permissionsList?.insertAllowed === 'Y' && <Button label="Add New" icon="pi pi-plus" className="p-button-success p-button-sm" onClick={() => handleAdd()} />}</div> */}
                {activeRequestTab === 'pending' ? (
                <div className="flex gap-2">
                    <Button
                        label={t('Add Spare Parts')}
                        icon="pi pi-plus"
                        className="p-button-success p-button-sm"
                        tooltip={t('Add a new part to inventory')}
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleAdd()}
                    />
                </div>
                ) : null}
            </div>

            <div className="mb-4">
                <div className="flex flex-wrap gap-4 mb-4" style={{ borderBottom: '1px solid #d9ddea' }}>
                    <button
                        type="button"
                        onClick={() => setActiveRequestTab('pending')}
                        className="p-link text-xl pb-2"
                        style={{
                            color: activeRequestTab === 'pending' ? '#0d7ff2' : '#8b90a6',
                            borderBottom: activeRequestTab === 'pending' ? '2px solid #0d7ff2' : '2px solid transparent',
                            fontWeight: 500
                        }}
                    >
                        {t('Solicitudes pendientes de repuestos')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveRequestTab('repairs')}
                        className="p-link text-xl pb-2"
                        style={{
                            color: activeRequestTab === 'repairs' ? '#0d7ff2' : '#8b90a6',
                            borderBottom: activeRequestTab === 'repairs' ? '2px solid #0d7ff2' : '2px solid transparent',
                            fontWeight: 500
                        }}
                    >
                        {t('Solicitudes para reparaciones de repuestos')}
                    </button>
                </div>

                {activeRequestTab === 'pending' ? (
                    <>
                    <div className="mb-4">
                        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <div>
                                <h4 className="m-0">{t('Pending Spare Part Requests')}</h4>
                                <small className="text-600">{t('Shared queue for requests created from repair orders.')}</small>
                            </div>
                            <span className="text-sm text-600">{pendingRequests.length} {t('pending')}</span>
                        </div>
                        <DataTable
                            value={pendingRequests}
                            paginator
                            rows={5}
                            rowsPerPageOptions={[5, 10, 20]}
                            emptyMessage={t('No pending spare part requests.')}
                            responsiveLayout="scroll"
                            className="p-datatable-sm mb-4"
                        >
                            <Column header={t('Requested At')} body={(rowData) => (rowData.createdAt ? new Date(rowData.createdAt).toLocaleString() : '-')} />
                            <Column header={t('Part')} body={(rowData) => `${rowData.sparePart?.sku || ''} – ${rowData.sparePart?.description || ''}`.trim() || '-'} style={{ minWidth: '16rem' }} />
                            <Column field="sparePart.category" header={t('Category')} body={(rowData) => translateUiText(rowData.sparePart?.category || '-', preferredLanguage)} />
                            <Column field="quantity" header={t('Qty')} />
                            <Column header={t('Stock Check')} body={stockStatusTemplate} style={{ minWidth: '10rem' }} />
                            <Column header={t('Machine')} body={(rowData) => rowData.machine?.serialNumber || rowData.machine?.uuid || rowData.machine?.location || '-'} />
                            <Column header={t('Technician')} body={(rowData) => `${rowData.technician?.firstName || ''} ${rowData.technician?.lastName || ''}`.trim() || '-'} />
                            <Column field="notes" header={t('Notes')} body={(rowData) => rowData.notes || '-'} style={{ minWidth: '14rem' }} />
                            <Column header={t('Actions')} body={requestActionsTemplate} />
                        </DataTable>
                    </div>

                    <div className="mb-4">
                        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <div>
                                <h4 className="m-0">{t('Reviewed Spare Part Requests')}</h4>
                                <small className="text-600">{t('Approved and rejected requests with full review history.')}</small>
                            </div>
                            <span className="text-sm text-600">{requestHistoryRows.length} {t('reviewed')}</span>
                        </div>
                        <DataTable
                            value={requestHistoryRows}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 20]}
                            emptyMessage={t('No reviewed spare part requests yet.')}
                            responsiveLayout="scroll"
                            className="p-datatable-sm mb-4"
                        >
                            <Column header={t('Requested At')} body={(rowData) => (rowData.createdAt ? new Date(rowData.createdAt).toLocaleString() : '-')} />
                            <Column header={t('Reviewed At')} body={(rowData) => (rowData.authorizationDate ? new Date(rowData.authorizationDate).toLocaleString() : '-')} />
                            <Column header={t('Status')} body={(rowData) => translateUiToken(rowData.status || '-', preferredLanguage)} />
                            <Column header={t('Part')} body={(rowData) => `${rowData.sparePartSKU || ''} – ${rowData.sparePartDescription || ''}`.trim() || '-'} style={{ minWidth: '16rem' }} />
                            <Column field="sparePartCategory" header={t('Category')} body={(rowData) => translateUiText(rowData.sparePartCategory || '-', preferredLanguage)} />
                            <Column field="quantity" header={t('Qty')} />
                            <Column header={t('Machine')} body={(rowData) => rowData.machineSerialNumber || rowData.machineUuid || rowData.machineLocation || '-'} />
                            <Column header={t('Technician')} body={(rowData) => `${rowData.technicianName || ''} ${rowData.technicianLastName || ''}`.trim() || '-'} />
                            <Column header={t('Approved By')} body={(rowData) => `${rowData.supervisorName || ''} ${rowData.supervisorLastName || ''}`.trim() || '-'} />
                            <Column field="notes" header={t('Request Notes')} body={(rowData) => rowData.notes || '-'} style={{ minWidth: '14rem' }} />
                            <Column field="reviewComment" header={t('Review Comment')} body={(rowData) => rowData.reviewComment || '-'} style={{ minWidth: '14rem' }} />
                            <Column header={t('Links')} body={sourceLinksTemplate} style={{ minWidth: '12rem' }} />
                        </DataTable>
                    </div>

                    <div className="mb-4">
                        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <div>
                                <h4 className="m-0">{t('Inventory Activity')}</h4>
                                <small className="text-600">{t('Stock ledger for manual changes, approvals, and imports.')}</small>
                            </div>
                            <span className="text-sm text-600">{inventoryActivityRows.length} {t('activity records')}</span>
                        </div>
                        <DataTable
                            value={inventoryActivityRows}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 20]}
                            emptyMessage={t('No inventory activity yet.')}
                            responsiveLayout="scroll"
                            className="p-datatable-sm mb-4"
                        >
                            <Column header={t('Timestamp')} body={(rowData) => (rowData.createdAt ? new Date(rowData.createdAt).toLocaleString() : '-')} />
                            <Column header={t('Action')} body={activityTypeTemplate} />
                            <Column header={t('Part')} body={(rowData) => `${rowData.skuSnapshot || ''} – ${rowData.descriptionSnapshot || ''}`.trim() || '-'} style={{ minWidth: '16rem' }} />
                            <Column field="quantityBefore" header={t('Before')} />
                            <Column header={t('Delta')} body={quantityDeltaTemplate} />
                            <Column field="quantityAfter" header={t('After')} />
                            <Column header={t('By')} body={activityPerformedByTemplate} />
                            <Column field="comment" header={t('Comment')} body={(rowData) => rowData.comment || '-'} style={{ minWidth: '14rem' }} />
                            <Column header={t('Links')} body={sourceLinksTemplate} style={{ minWidth: '12rem' }} />
                        </DataTable>
                    </div>

                    <div className="grid p-fluid mb-3">
                        <div className="col-12 md:col-4">
                            <label htmlFor="skuFilter" className="block mb-2">{t('Filter by SKU')}</label>
                            <InputText
                                id="skuFilter"
                                value={filters.sku}
                                onChange={(e) => setFilters((prev) => ({ ...prev, sku: e.target.value }))}
                                placeholder={t('Type SKU')}
                            />
                        </div>
                        <div className="col-12 md:col-4">
                            <label htmlFor="categoryFilter" className="block mb-2">{t('Filter by Category')}</label>
                            <Dropdown
                                id="categoryFilter"
                                value={filters.category}
                                options={categoryOptions}
                                onChange={(e) => setFilters((prev) => ({ ...prev, category: e.value }))}
                                placeholder={t('Select category')}
                                showClear
                                className="w-full"
                            />
                        </div>
                        <div className="col-12 md:col-4">
                            <label htmlFor="descriptionFilter" className="block mb-2">{t('Filter by Description')}</label>
                            <InputText
                                id="descriptionFilter"
                                value={filters.description}
                                onChange={(e) => setFilters((prev) => ({ ...prev, description: e.target.value }))}
                                placeholder={t('Type description')}
                            />
                        </div>
                        <div className="col-12 flex justify-content-end">
                            <Button
                                label={t('Clear Filters')}
                                icon="pi pi-filter-slash"
                                className="p-button-text p-button-sm"
                                onClick={() => setFilters({ sku: '', category: null, description: '' })}
                            />
                        </div>
                    </div>

                    <DataTable
                        paginator
                        rows={15}
                        rowsPerPageOptions={[5, 10, 20]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate={t('Showing {first} to {last} of {totalRecords} Records')}
                        emptyMessage={t('No parts inventory found.')}
                        responsiveLayout="scroll"
                        value={filteredPartsInventory}
                        className="p-datatable-sm datatable-responsive"
                    >
                        <Column field="orderNumber" header={t('Order Number')} />
                        <Column field="orderDate" header={t('Order Date')} body={(rowData) => new Date(rowData.orderDate).toLocaleDateString()} />
                        <Column field="sku" header="SKU" />
                        <Column field="description" header={t('Description')} style={{ minWidth: '18rem' }} />
                        <Column field="location" header={t('Location')} />
                        <Column field="condition" header={t('Condition')} body={(rowData) => translateUiText(rowData.condition || '-', preferredLanguage)} />
                        <Column field="category" header={t('Category')} body={(rowData) => translateUiText(rowData.category || '-', preferredLanguage)} />
                        <Column
                            field="quantityInStock"
                            header={t('Quantity')}
                            body={(rowData) => (
                                <span
                                    style={{
                                        color: rowData.quantityInStock < 10 ? 'red' : rowData.quantityInStock < 20 ? 'orange' : 'green',
                                        fontWeight: rowData.quantityInStock < 10 ? 'bold' : 'normal'
                                    }}
                                >
                                    {rowData.quantityInStock}
                                </span>
                            )}
                        />
                        <Column field="pricePerUnit" header={t('Price/Unit')} body={(rowData) => `$${rowData.pricePerUnit?.toFixed(2)}`} />

                        <Column header={t('Actions')} body={actionBodyTemplate} className="Table__ActionHeading__CentredAlign" />
                    </DataTable>
                    </>
                ) : (
                    <SparePartRepairTasks
                        partsInventory={partsInventory}
                        preferredLanguage={preferredLanguage}
                        onInventoryChanged={refreshInventoryAfterRepair}
                    />
                )}
            </div>

        </div>
    );
};

export default PartsInventory;
